"use client";

import { getEventIdentityProps } from "@/lib/analytics/identity";
import { useDiscoverContextProps } from "@/hooks/useDiscoverContextProps";
import { useTrackOnceConsented } from "@/hooks/useTrackOnceConsented";

/**
 * Fires "View discover event" from the browser.
 *
 * The event detail page used to send this during render, which meant reading the
 * consent cookie, the distinct-id cookie, the attribution cookie and the
 * `referer` header. On a cached page those all come back empty, so the event
 * stopped being sent at all rather than merely being slow to send.
 *
 * `/api/analytics/track` still applies the consent gate and resolves the distinct
 * id server-side, so the visitor is identified exactly as before.
 *
 * Render inside a `<Suspense>` boundary: `useSearchParams` otherwise opts the
 * subtree out of static rendering.
 */
export function EventViewTracker({
  eventId,
  clubId,
  groupId,
  sourceType,
  isPublicEvent,
}: {
  eventId: string;
  clubId?: string | null;
  groupId?: string | null;
  sourceType?: string | null;
  isPublicEvent: boolean;
}) {
  const getContextProps = useDiscoverContextProps("events");

  useTrackOnceConsented(
    "View discover event",
    () => ({
      ...getEventIdentityProps({
        eventId,
        clubId,
        groupId,
        sourceType,
        isPublicEvent,
      }),
      ...getContextProps(),
    }),
    { key: eventId },
  );

  return null;
}
